import { FilterColors, FilterFonts } from "../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { ReactElement, memo, useContext, useRef } from "react";
import { elementAlign, opacityHelper } from "../../lib/cssHelpers";
import styled, { DefaultTheme, ThemeProvider } from "styled-components";

import { ElementProps } from "../../functionalComponents/RenderPage";
import { HostId } from "../../state/ui";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import MarkDownToTag from "../../lib/markdown/MarkDown";
import { OwnerName } from "../../state/selectors";
import { SubTitleProps } from "../../@types/viewerati";
import { mainCssRules } from "../../lib/cssTemplates";
import modifierStyled from "../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../state/accessibility";
import { strings } from "@quickpoint.me/shared";
import useAiAnimation from "../../lib/animations/aiAnimationHook";
import { useRecoilValue } from "recoil";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";

const subtitleFilter = (theme: DefaultTheme) => {
	return theme.subtitle;
};

const StyledSubTitle = styled.h3<SubTitleProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
  margin: 0;
  text-wrap: auto;
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  ${(props) => elementAlign(props.theme.align, props.$isInFlexBox)};
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  position: relative;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`};
  ${(props) => `${modifierStyled("subtitle:", props)}`}
`;

const SubTitle = ({
	atomId,
	pageIndex,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const elRef = useRef(null);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const pageSize = useRecoilValue(PageSizeAtom);
	const fonts = useRecoilValue(FilterFonts);
	const baseFontSize = pageSize.baseFontSize;
	const tapped = elementData.tapped;
	const ownerName = useRecoilValue(OwnerName);
	const hostId = useRecoilValue(HostId);

	useTappedAnimation({ elRef, tapped, atomId });
	useAiAnimation({ elRef, modifier: elementData.modifier });

	const content =
		typeof elementData.content === "string"
			? MarkDownToTag({
					text: elementData.content,
					ownerName,
					modifier: elementData.modifier,
					hostId,
					tapped,
				})
			: elementData.content;

	if (!elementData.content) return <></>;
	return (
		<ThemeProvider theme={subtitleFilter}>
			<StyledSubTitle
				as={pageIndex === 1 ? "h2" : "h3"}
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$isInFlexBox={isInFlexBox}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				className={`${strings.css.pageElement}  subtitle`}
				data-atom-id={atomId}
				ref={elRef}
			>
				{content}
			</StyledSubTitle>
		</ThemeProvider>
	);
};

export default memo(SubTitle);
